import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, homeFor } from "@/context/AuthContext";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/common";
import { toast } from "sonner";
import { Building2 } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      // 1) Identifier le type de compte à partir de l'email
      let lookup = null;
      try {
        const { data } = await api.get(`/auth/portal-lookup`, { params: { email } });
        lookup = data;
      } catch { /* en cas d'échec du lookup, on tente la connexion normale */ }

      // 2) Client portail -> redirection vers son portail dédié (email pré-rempli)
      if (lookup?.is_client && lookup?.portal_slug) {
        toast.success("Redirection vers votre espace client…");
        navigate(`/${lookup.portal_slug}?email=${encodeURIComponent(email)}`);
        return;
      }

      // 2b) Client dont le portail est indisponible (société manquante)
      if (lookup?.is_client && !lookup?.portal_slug) {
        setError("Votre espace client est momentanément indisponible. Merci de contacter le support.");
        return;
      }

      // 3) Email inconnu -> message d'aide clair
      if (lookup && lookup.found === false) {
        setError("Email non reconnu. Si vous êtes client, merci d'utiliser le lien fourni par votre administrateur ou contactez le support.");
        return;
      }

      // 4) Staff -> connexion normale sur /login
      const user = await login(email, password);
      navigate(homeFor(user));
    } catch (err) {
      setError(fmtErr(err, "Connexion impossible"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-dvh bg-background" data-testid="login-page">
      <div className="flex w-full flex-col justify-center px-6 sm:px-16 lg:w-1/2">
        <div className="mx-auto w-full max-w-md">
          <div className="mb-10">
            <div className="font-heading text-2xl font-semibold">Suivio<span className="text-primary">360</span></div>
            <h1 className="mt-8 text-3xl font-semibold tracking-tight">Connexion équipe</h1>
            <p className="mt-2 text-sm text-muted-foreground">Espace réservé aux équipes et administrateurs. Client ? Utilisez le portail de votre société.</p>
          </div>
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email professionnel</Label>
              <Input id="email" data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="prenom@entreprise.com" className="rounded-sm" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Mot de passe</Label>
                <Link to="/mot-de-passe-oublie" data-testid="forgot-password-link"
                  className="text-xs font-medium text-primary hover:underline">Mot de passe oublié ?</Link>
              </div>
              <PasswordInput id="password" testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            {error && <div data-testid="login-error" className="rounded-sm border border-primary/40 bg-[hsl(var(--note-bg))] px-4 py-3 text-sm text-primary">{error}</div>}
            <Button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full rounded-sm bg-primary text-white hover:bg-red-700 transition-colors duration-200">
              {loading ? "Connexion…" : "Se connecter"}
            </Button>
          </form>
          <div className="mt-8 border-t border-border pt-6 text-center">
            <p className="text-xs text-muted-foreground">Vous êtes client d'une entreprise ?</p>
            <Link to="/portail-client" data-testid="client-portal-access-link"
              className="mt-2 inline-flex items-center gap-2 rounded-sm border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary hover:text-primary">
              <Building2 className="h-4 w-4" /> Accès Portail Client
            </Link>
          </div>
        </div>
      </div>
      <div className="relative hidden w-1/2 lg:block">
        <img src="https://images.pexels.com/photos/13845232/pexels-photo-13845232.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative flex h-full flex-col justify-end p-16">
          <p className="max-w-md font-heading text-3xl font-light leading-snug text-white">
            Centralisez, suivez et traitez <span className="font-semibold text-primary">toutes vos demandes clients.</span>
          </p>
          <p className="mt-4 text-sm text-white/60">Suivio360 — le lieu officiel de traitement de vos tickets.</p>
        </div>
      </div>
    </div>
  );
}
